package deckgl.util

import org.locationtech.jts.geom.Coordinate
import org.locationtech.jts.geom.Envelope
import kotlin.math.*

/**
 * Calculates the centroid of a geographic bounding box in the Mercator projection.
 * @param envelope JTS Envelope defining the bounding box with minX/maxX (longitude) and minY/maxY (latitude) in degrees
 * @return Coordinate containing the centroid (x: longitude, y: latitude) in degrees
 */
fun calculateMercatorCentroid(envelope: Envelope): Coordinate {
    // Handle degenerate cases
    if (envelope.minY == envelope.maxY || envelope.minX == envelope.maxX) {
        return Coordinate((envelope.minX + envelope.maxX) / 2, (envelope.minY + envelope.maxY) / 2)
    }

    // Clamp latitudes to avoid Mercator projection issues near poles
    val clampLat = { lat: Double -> lat.coerceIn(-85.05112878, 85.05112878) }
    val clampedMinLat = clampLat(envelope.minY)
    val clampedMaxLat = clampLat(envelope.maxY)

    // Convert latitude to Mercator Y-coordinate
    fun latToY(lat: Double): Double {
        val sinLat = sin(lat * PI / 180.0)
        return 0.5 - ln((1 + sinLat) / (1 - sinLat)) / (4 * PI)
    }

    // Convert Mercator Y-coordinate back to latitude
    fun yToLat(y: Double): Double {
        val term = exp(4 * PI * (0.5 - y))
        return asin((term - 1) / (term + 1)) * 180.0 / PI
    }

    // Calculate Mercator Y-coordinates for min and max latitudes
    val latYMin = latToY(clampedMinLat)
    val latYMax = latToY(clampedMaxLat)

    // Calculate centroid in Mercator space
    val mercatorCenterY = (latYMin + latYMax) / 2
    val centerLon = ((envelope.minX + envelope.maxX + 360) % 360) / 2

    // Convert Mercator Y back to latitude
    val centerLat = yToLat(mercatorCenterY)

    return Coordinate(centerLon, centerLat)
}

fun calculateZoomLevel(
    minLon: Double,
    minLat: Double,
    maxLon: Double,
    maxLat: Double,
    width: Int,
    height: Int,
    margin: Double = 0.0,
    tile_size: Int = 512
): Double {

    // Handle degenerate cases
    if (minLat == maxLat || minLon == maxLon) return 21.0

    // Expand bounds
    val lonMargin = (maxLon - minLon) * margin
    val latMargin = (maxLat - minLat) * margin

    val expandedMinLon = minLon - lonMargin
    val expandedMaxLon = maxLon + lonMargin
    val expandedMinLat = minLat - latMargin
    val expandedMaxLat = maxLat + latMargin

    // Clamp latitudes
    val clampLat = { lat: Double -> lat.coerceIn(-85.05112878, 85.05112878) }

    fun latToY(lat: Double): Double {
        val sinLat = sin(lat * PI / 180.0)
        return 0.5 - ln((1 + sinLat) / (1 - sinLat)) / (4 * PI)
    }

    val latYMin = latToY(clampLat(expandedMinLat))
    val latYMax = latToY(clampLat(expandedMaxLat))
    val latFraction = abs(latYMax - latYMin)

    val lonSpan = ((expandedMaxLon - expandedMinLon + 360) % 360).coerceAtMost(360.0)
    val lonFraction = lonSpan / 360.0

    val latZoom = log2(height / tile_size.toDouble() / latFraction)
    val lonZoom = log2(width / tile_size.toDouble() / lonFraction)

    return min(min(latZoom, lonZoom), 22.0)
}